import { useState, useEffect } from 'react';

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    // Detect platform for PWA support
    const detectPlatform = () => {
      const userAgent = navigator.userAgent;
      
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isIOSSafari = isIOS && /Safari/.test(userAgent) && !/CriOS|FxiOS/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isChrome = /Chrome/.test(userAgent) && !/Edg/.test(userAgent);
      const isEdge = /Edg/.test(userAgent);
      
      if (isIOSSafari) return 'ios-safari';
      if (isAndroid && (isChrome || isEdge)) return 'android-chrome';
      if (!isIOS && !isAndroid && isChrome) return 'desktop-chrome';
      if (!isIOS && !isAndroid && isEdge) return 'desktop-edge';
      return 'unsupported';
    };

    // Check if app is already installed
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone ||
                          document.referrer.includes('android-app://') ||
                          window.matchMedia('(display-mode: minimal-ui)').matches;
      
      setIsInstalled(isStandalone);
      if (isStandalone) {
        setIsInstallable(false);
        setUserDismissed(false);
      }
    };

    // Check if user previously dismissed the prompt
    const checkUserDismissed = () => {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      
      if (dismissedTime && dismissedTime < oneDayAgo) {
        localStorage.removeItem('pwa-install-dismissed');
        setUserDismissed(false);
      } else {
        setUserDismissed(!!dismissed);
      }
    };

    // Event handlers
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-dismissed');
    };

    // Initialize
    const detectedPlatform = detectPlatform();
    setPlatform(detectedPlatform);
    checkIfInstalled();
    checkUserDismissed();

    // Setup for supported platforms
    if (detectedPlatform === 'android-chrome' || detectedPlatform === 'desktop-chrome' || detectedPlatform === 'desktop-edge') {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
      
      // Fallback timeout
      setTimeout(() => {
        if (!deferredPrompt && !isInstalled) {
          setIsInstallable(true);
        }
      }, 3000);
    } else if (detectedPlatform === 'ios-safari') {
      setIsInstallable(true);
    }

    // Periodic install check
    const installCheckInterval = setInterval(checkIfInstalled, 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearInterval(installCheckInterval);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return 'fallback';

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        // Success handled by appinstalled event
      } else {
        dismissInstallPrompt();
      }
      
      setDeferredPrompt(null);
      setIsInstallable(false);
      return outcome === 'accepted';
    } catch (error) {
      return false;
    }
  };

  const dismissInstallPrompt = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setUserDismissed(true);
    setIsInstallable(false);
  };

  const showInstallButton = isInstallable && !isInstalled && !userDismissed && platform !== 'unsupported';

  return {
    isInstallable,
    isInstalled,
    showInstallButton,
    platform,
    installApp,
    dismissInstallPrompt
  };
};